import { ReactNode } from 'react';
import { Trans } from '@lingui/react/macro';
import isNil from 'lodash/isNil';

import { PriceAmountWithDetails, PriceAmountWithDetailsWithConversions } from '@/api';
import { CardItem } from '@/components/Card/Card';
import { PriceWithDetailsTooltipWithConversions } from '@/components/PriceWithDetails/PriceWithDetails';
import Badge from '@/design_system/Badge';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconCross from '@/icons/Cross.svg';
import IconPay from '@/icons/Pay.svg';
import { ArticleWithRelations } from '@/models/request';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { formatCurrency } from '@/utils/number';
import { shouldShowPendingOrganizationQuote, shouldShowPendingWorkshopQuote } from '@/utils/price';
import useViewPort from '@/utils/useViewport';

const ClientRefusalBadge = ({ text }: { text: string }) => (
  <Badge color="danger" size="small" noWrap hasBorder borderRadius="soft">
    <IconCross />
    {text}
  </Badge>
);

const BaseMoneyCell = ({
  price,
  label,
  clientRefusalBadgeText,
  isInvalid,
  showAmountBeforeTaxes,
  size,
  isDisabled = false,
  emptyLabel,
}: {
  price: PriceAmountWithDetailsWithConversions | PriceAmountWithDetails | undefined | null;
  label?: string;
  clientRefusalBadgeText?: string;
  className?: string;
  isInvalid?: boolean;
  showAmountBeforeTaxes: boolean;
  size?: 'x-small' | 'small' | 'medium';
  isDisabled?: boolean;
  emptyLabel?: ReactNode;
}) => {
  const { isMobile } = useViewPort();
  const resolvedEmptyLabel = emptyLabel ?? (
    <Trans id="article.price.pending-quote">Pending quote</Trans>
  );

  if (isMobile) {
    return (
      <Stack alignItems="flex-end" row gap="0.25rem">
        {label && (
          <Paragraph size="200" color="secondary">
            {label}
          </Paragraph>
        )}
        {!isNil(price?.amount) ? (
          <Stack row flexWrap="nowrap" gap="0.25rem" alignItems="center">
            {clientRefusalBadgeText ? (
              <ClientRefusalBadge text={clientRefusalBadgeText} />
            ) : price.cancelled || isDisabled ? (
              <s className="text-disabled">
                {formatCurrency(
                  showAmountBeforeTaxes ? price.amountBeforeTaxes : price.amount,
                  price.currency
                )}
              </s>
            ) : (
              <PriceWithDetailsTooltipWithConversions
                price={price}
                options={{ showAmountBeforeTaxes }}
                size={size}
                direction="row-reverse"
              />
            )}
          </Stack>
        ) : (
          <Paragraph size="200" color={isInvalid ? 'danger' : 'primary'}>
            {resolvedEmptyLabel}
          </Paragraph>
        )}
      </Stack>
    );
  }

  return (
    <Stack alignItems="flex-end">
      {label && <Paragraph color="secondary">{label}</Paragraph>}
      {!isNil(price?.amount) ? (
        <Stack row alignItems="center" style={{ whiteSpace: 'nowrap' }} gap="0.25rem">
          {clientRefusalBadgeText ? (
            <ClientRefusalBadge text={clientRefusalBadgeText} />
          ) : price.cancelled || isDisabled ? (
            <InlineText color="disabled">
              <s>
                {formatCurrency(
                  showAmountBeforeTaxes ? price.amountBeforeTaxes : price.amount,
                  price.currency
                )}
              </s>
            </InlineText>
          ) : (
            <PriceWithDetailsTooltipWithConversions
              price={price}
              options={{ showAmountBeforeTaxes }}
              size={size}
              direction="row-reverse"
            />
          )}
        </Stack>
      ) : (
        <Paragraph size="200" align="right">
          {resolvedEmptyLabel}
        </Paragraph>
      )}
    </Stack>
  );
};

export const ArticleWorkshopPriceCardItem = ({ article }: { article: ArticleWithRelations }) => {
  const { view } = useRequestContext();

  return (
    <CardItem>
      <IconPay payOut style={{ fontSize: '1rem' }} />
      <span>
        {view.workshopPrice.label}
        <Trans id="_general.colon">:</Trans>
      </span>
      <BaseMoneyCell
        price={article.snapshot.workshopPrice?.amountPerEntity?.find(
          (amount) => amount.entityId === article.workshopId
        )}
        showAmountBeforeTaxes={view.workshopPrice.showAmountBeforeTaxes}
        size="x-small"
        emptyLabel={shouldShowPendingWorkshopQuote(article) ? undefined : '-'}
      />
    </CardItem>
  );
};

export const ArticleOrganizationPriceCardItem = ({
  article,
  clientRefusalBadgeText,
}: {
  article: ArticleWithRelations;
  clientRefusalBadgeText?: string;
}) => {
  const { view } = useRequestContext();

  return (
    <CardItem>
      <IconPay payIn style={{ fontSize: '1rem' }} />
      <span>
        {view.organizationPrice.label}
        <Trans id="_general.colon">:</Trans>
      </span>
      <BaseMoneyCell
        price={article.snapshot.organizationPrice?.amountPerCurrency?.[0]}
        clientRefusalBadgeText={clientRefusalBadgeText}
        showAmountBeforeTaxes={view.organizationPrice.showAmountBeforeTaxes}
        size="x-small"
        emptyLabel={shouldShowPendingOrganizationQuote(article) ? undefined : '-'}
      />
    </CardItem>
  );
};

export default BaseMoneyCell;
